import * as React from "react";
import type { Editor as TinyMCEEditor } from "tinymce";
import { UIModel } from "../../../../model/ui-model";
import { RoleBasedPermissionedContentEditModule } from "../custom-modules/RoleBasedPermissionedContentModule";
import { DEFAULT_CONFIG } from "./config/default";
import { TinyMCEBaseEditor } from "./TinyMCEBaseEditor";
import { setupMarkdownPaste } from "./utils/markdown-paste-handler";

export const StrongRichTextEdit = (props: {
    content: string;
    onChange: (v: string) => void;
    hidePermContent?: boolean;
}) => {
    const permissionContentActive = UIModel.State.use<boolean>({ initialValue: false });
    const [editor, setEditor] = React.useState<TinyMCEEditor | null>(null);

    const getTinyConfig = () => {
        const setupCallbacks: Array<(editor: TinyMCEEditor) => void> = [];

        let config = DEFAULT_CONFIG;

        // The editor runs in open-source (free/GPL) mode; register the markdown-it paste
        // handler in place of the premium markdown/powerpaste plugins.
        setupCallbacks.push((e) => setupMarkdownPaste(e));

        setupCallbacks.push((e) => {
            if (props.hidePermContent) return;
            e.ui.registry.addButton("permissionedContentButton", {
                icon: "lock",
                text: "Permissioned Content",
                onAction: val => permissionContentActive.setValue(true)
            });
        });

        config = {
            ...config,
            toolbar: config.toolbar + "|permissionedContentButton",
            setup: (e) => setupCallbacks.forEach(cb => cb(e)),
            paste_preprocess: (plugin, args) => {
                let restoreHtml = args.content;
                restoreHtml = restoreHtml.replaceAll("&lt;", "<");
                restoreHtml = restoreHtml.replaceAll("&gt;", ">");
                restoreHtml = restoreHtml.replaceAll("&quot;", `"`);
                args.content = restoreHtml;
            }
        } as typeof config;

        return config;
    };

    const [tipDismissed, setTipDismissed] = React.useState(
        () => localStorage.getItem("richTextEditor.tipDismissed") === "true"
    );
    const isMac = /mac/i.test(navigator.userAgent);
    const key = isMac ? "⌘" : "Ctrl";

    return (
        <React.Fragment>
            <RoleBasedPermissionedContentEditModule active={permissionContentActive} editor={editor}/>
            {!tipDismissed && (
                <div style={{ padding: "6px 12px", backgroundColor: "#d4edfa", borderRadius: 4, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                    <span>Tip: Use {key}+Shift+V to paste markdown as formatted text</span>
                    <button
                        onClick={() => { localStorage.setItem("richTextEditor.tipDismissed", "true"); setTipDismissed(true); }}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
                        aria-label="Dismiss tip"
                    >×</button>
                </div>
            )}
            <TinyMCEBaseEditor
                onChange={props.onChange}
                value={props.content}
                onInit={e => setEditor(e)}
                config={getTinyConfig()}
            />
        </React.Fragment>
    );
};